import React, { useCallback, useEffect, useState } from 'react';
import { questionAPI } from '../../../api';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const XLSX = require('xlsx');

const AdminQuestions: React.FC = () => {
  const [subjects, setSubjects] = useState<any[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<number | ''>('');
  const [examType, setExamType] = useState<'필기' | '실기'>('필기');
  const [files, setFiles] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadLog, setUploadLog] = useState<string[]>([]);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [imagePath, setImagePath] = useState(() => localStorage.getItem('mohabwe_image_path') || '');
  const [imagePathSaved, setImagePathSaved] = useState(false);

  const saveImagePath = () => {
    localStorage.setItem('mohabwe_image_path', imagePath);
    setImagePathSaved(true);
    setTimeout(() => setImagePathSaved(false), 2000);
  };

  const loadSubjects = useCallback(() => {
    questionAPI.adminSubjects().then(res => setSubjects(res.data));
  }, []);

  const loadFiles = useCallback(() => {
    if (!selectedSubject) { setFiles([]); return; }
    questionAPI.adminQuestionFiles(selectedSubject as number)
      .then(res => setFiles(res.data.filter((f: any) => f.exam_type === examType)));
  }, [selectedSubject, examType]);

  useEffect(() => { loadSubjects(); }, [loadSubjects]);
  useEffect(() => { loadFiles(); }, [loadFiles]);

  const log = (msg: string) => setUploadLog(prev => [...prev, msg]);

  // 엑셀 업로드
  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selectedSubject) { alert('종목을 먼저 선택해주세요.'); return; }

    // 선택한 파일명 저장
    setSelectedFileName(file.name);

    setUploading(true);
    setUploadLog([]);
    log(`📂 ${file.name} 읽는 중...`);

    try {
      const ab = await file.arrayBuffer();
      const wb = XLSX.read(ab, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });

      if (rows.length < 2) { log('❌ 데이터가 없습니다.'); setUploading(false); return; }

      const headers = rows[0].map((h: any) => String(h || '').trim());
      log(`✅ 헤더 확인: ${headers.join(', ')}`);

      const getCol = (row: any[], ...names: string[]) => {
        for (const name of names) {
          const idx = headers.indexOf(name);
          if (idx >= 0 && row[idx] !== undefined && row[idx] !== null) {
            return String(row[idx]).trim();
          }
        }
        return '';
      };

      // 숫자형 년도/차수 소수점 제거 (예: 2004.0 → 2004)
      const getNum = (row: any[], ...names: string[]) => {
        const val = getCol(row, ...names);
        if (!val) return '';
        const num = parseFloat(val);
        return isNaN(num) ? val : String(Math.round(num));
      };

      // 년도/차수별로 그룹핑
      const groups: Record<string, any[]> = {};
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (!row || row.every((c: any) => !c)) continue;
        const year = getNum(row, '년도') || '미지정';
        const round = getNum(row, '차수') || '미지정';
        const key = `${year}_${round}`;
        if (!groups[key]) groups[key] = [];
        groups[key].push(row);
      }

      const groupKeys = Object.keys(groups);
      log(`📊 총 ${rows.length - 1}개 문제, ${groupKeys.length}개 그룹(년도/차수) 감지`);

      // 그룹별 업로드
      for (const key of groupKeys) {
        const [year, round] = key.split('_');
        const groupRows = groups[key];
        log(`\n📤 ${year}년 ${round}차 (${groupRows.length}개) 업로드 중...`);

        // FormData 구성
        const formData = new FormData();
        formData.append('subject_id', String(selectedSubject));
        formData.append('exam_type', examType);
        formData.append('year', year);
        formData.append('round', round);
        formData.append('overwrite', 'true');

        // 해당 그룹 데이터만 엑셀로 재생성해서 업로드
        const newWb = XLSX.utils.book_new();
        const newData = [headers, ...groupRows];
        const newWs = XLSX.utils.aoa_to_sheet(newData);
        XLSX.utils.book_append_sheet(newWb, newWs, 'Sheet1');
        const wbOut = XLSX.write(newWb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbOut], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        formData.append('file', blob, `${year}_${round}.xlsx`);

        try {
          const res = await questionAPI.uploadQuestions(formData);
          log(`✅ ${year}년 ${round}차 → ${res.data.question_count}개 저장 완료`);
        } catch (err: any) {
          const msg = err.response?.data?.error || err.message;
          log(`❌ ${year}년 ${round}차 실패: ${msg}`);
        }
      }

      log('\n🎉 업로드 완료!');
      loadFiles();
    } catch (err: any) {
      log(`❌ 엑셀 읽기 실패: ${err.message}`);
    }

    setUploading(false);
    e.target.value = '';
  };

  // 전체 일괄 삭제
  const deleteAllFiles = async () => {
    if (files.length === 0) return;
    if (!window.confirm(`${selectedSubjectName} ${examType} 문제 전체 ${files.length}개 파일을 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다!`)) return;
    try {
      await Promise.all(files.map(f => questionAPI.deleteQuestionFile(f.id)));
      alert(`${files.length}개 파일이 모두 삭제되었습니다.`);
      loadFiles();
    } catch (e) { alert('일부 삭제 실패'); loadFiles(); }
  };

  // 문제파일 삭제
  const deleteFile = async (fileId: number, label: string) => {
    if (!window.confirm(`${label} 문제를 삭제하시겠습니까?`)) return;
    try {
      const res = await questionAPI.deleteQuestionFile(fileId);
      alert(res.data.message);
      loadFiles();
    } catch (e) { alert('삭제 실패'); }
  };

  const selectedSubjectName = subjects.find(s => s.id === selectedSubject)?.name || '';

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title"><span className="icon">❓</span>문제 데이터 관리</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>년도/차수별 업로드 및 관리</div>
      </div>

      {/* 이미지 폴더 경로 설정 */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">🖼️ 이미지 폴더 경로 설정</div>
        <div className="card-body">
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
            문제 이미지 파일이 저장된 폴더 경로를 입력하세요. 저장하면 다음에도 유지됩니다.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="form-control"
              style={{ flex: 1, minWidth: 300, fontFamily: 'monospace', fontSize: 13 }}
              placeholder="예: C:\JKCHO\Documents\MOHABWE\mohabwe-frontend\public\images"
              value={imagePath}
              onChange={e => setImagePath(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && saveImagePath()}
            />
            <button className="btn btn-primary" onClick={saveImagePath}>
              💾 저장
            </button>
            {imagePathSaved && (
              <span style={{ fontSize: 12, color: 'var(--success)', fontWeight: 700 }}>✅ 저장됨!</span>
            )}
          </div>
          {imagePath && (
            <div style={{ marginTop: 8, padding: '6px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, fontSize: 12, color: '#15803d', fontFamily: 'monospace' }}>
              📁 {imagePath}
            </div>
          )}
        </div>
      </div>

      {/* 종목/시험유형 선택 */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">📚 종목 선택</div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              className="form-select"
              style={{ minWidth: 200 }}
              value={selectedSubject}
              onChange={e => setSelectedSubject(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">-- 종목 선택 --</option>
              {subjects.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['필기', '실기'] as const).map(type => (
                <button
                  key={type}
                  className={`btn ${examType === type ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setExamType(type)}
                >
                  {type === '필기' ? '📝' : '🔧'} {type}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 엑셀 업로드 */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">📂 문제 엑셀 업로드</div>
        <div className="card-body">
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.8 }}>
            <strong>엑셀 컬럼 형식:</strong> 과목 | 년도 | 차수 | 번호 | 유형 | 난이도 | 문제 | 보기1 | 보기2 | 보기3 | 보기4 | 정답 | 해설 | 동영상
            <br />※ 년도/차수별로 자동 분리되어 저장됩니다. 기존 데이터는 덮어씁니다.
          </div>

          {!selectedSubject ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>⚠️ 종목을 먼저 선택해주세요.</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <label className={`btn btn-primary ${uploading ? 'disabled' : ''}`} style={{ cursor: uploading ? 'not-allowed' : 'pointer', margin: 0 }}>
                {uploading ? '⏳ 업로드 중...' : `📂 ${selectedSubjectName} ${examType} 엑셀 선택`}
                <input
                  type="file" accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handleUpload}
                  disabled={uploading}
                />
              </label>
              {/* 선택한 파일명 표시 */}
              {selectedFileName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, fontSize: 12, color: '#15803d', maxWidth: 400 }}>
                  <span style={{ fontSize: 14 }}>📄</span>
                  <span style={{ fontWeight: 600, wordBreak: 'break-all' }}>{selectedFileName}</span>
                </div>
              )}
            </div>
          )}

          {/* 업로드 로그 */}
          {uploadLog.length > 0 && (
            <div style={{
              marginTop: 16, padding: 12, background: '#1e293b', borderRadius: 8,
              fontFamily: 'monospace', fontSize: 12, color: '#94a3b8',
              maxHeight: 200, overflowY: 'auto', whiteSpace: 'pre-wrap',
              lineHeight: 1.8,
            }}>
              {uploadLog.map((line, i) => (
                <div key={i} style={{
                  color: line.startsWith('✅') ? '#4ade80'
                    : line.startsWith('❌') ? '#f87171'
                    : line.startsWith('🎉') ? '#fbbf24'
                    : '#94a3b8'
                }}>{line}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 등록된 문제파일 목록 */}
      {selectedSubject && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>📋 {selectedSubjectName} {examType} 문제 현황</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>총 {files.length}개 파일</span>
              {files.length > 0 && (
                <button className="btn btn-danger btn-sm" onClick={deleteAllFiles}>
                  🗑️ 전체 삭제
                </button>
              )}
            </div>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {files.length === 0 ? (
              <div className="empty-state" style={{ padding: 40 }}>
                <div className="icon">📭</div>
                <p>등록된 문제가 없습니다.</p>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>엑셀 파일을 업로드해주세요.</p>
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>년도</th>
                    <th>차수</th>
                    <th style={{ textAlign: 'center' }}>문제수</th>
                    <th>업로드일시</th>
                    <th style={{ textAlign: 'center' }}>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {files
                    .sort((a, b) => a.year.localeCompare(b.year) || a.round.localeCompare(b.round))
                    .map((f: any) => (
                      <tr key={f.id}>
                        <td><strong>{f.year}년</strong></td>
                        <td>{f.round}차</td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="badge" style={{ background: '#dbeafe', color: '#1d4ed8' }}>
                            {f.question_count}문제
                          </span>
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {f.uploaded_at?.slice(0, 16).replace('T', ' ')}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => deleteFile(f.id, `${f.year}년 ${f.round}차`)}
                          >
                            🗑️ 삭제
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuestions;
